import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProductAttribute, ProductAttributeType } from '@core/types';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import {
  atualizarCaracteristica, criarCaracteristica, listarCaracteristicas,
} from '../lib/consultas';
import { mensagemDeErro } from '../lib/supabase';

// Tela 12 — Características de peça. Só o dono chega aqui.
//
// O catálogo tem colunas fixas (posição, NCM, peso...), e cada campo novo
// custava migração e deploy. Aqui o dono cria a característica que a loja
// precisa — "lado", "voltagem", "rosca", "medida da correia" — e ela aparece
// sozinha no cadastro de peça e no filtro da busca.
//
// POR QUE SÓ O DONO: isto não é conteúdo de peça, é a FORMA do catálogo.
// Criar "lado" muda o cadastro de todo mundo. Quem preenche o valor em cada
// peça é qualquer papel (inclusive o vendedor, no cadastro just-in-time do
// balcão) — essa separação está na RLS (rls.sql §12), e a tela só a espelha.
//
// DESATIVAR, NÃO EXCLUIR: característica já preenchida em peça, se apagada,
// levaria o valor junto por cascata. Desativada ela some do formulário e do
// filtro, mas o que já foi gravado continua no banco — reativar traz tudo de
// volta. Por isso não existe botão de excluir aqui.

const TIPOS: Array<{ id: ProductAttributeType; rotulo: string; resumo: string }> = [
  { id: 'texto', rotulo: 'Texto livre', resumo: 'Quem cadastra digita o que quiser. Bom para medida e observação.' },
  { id: 'lista', rotulo: 'Lista de opções', resumo: 'Você define as opções e quem cadastra escolhe. É o que faz o filtro da busca funcionar bem.' },
  { id: 'numero', rotulo: 'Número', resumo: 'Digitação numérica no celular do balcão. Guardado como texto.' },
];

export default function Caracteristicas() {
  const cliente = useQueryClient();
  const [abrindoNova, setAbrindoNova] = useState(false);

  // apenasAtivas = false: esta é a tela que precisa enxergar as desativadas
  // para poder reativá-las. O formulário de peça e a busca pedem só as ativas.
  const lista = useQuery({
    queryKey: ['caracteristicas', 'todas'],
    queryFn: () => listarCaracteristicas(false),
  });

  const recarregar = () => {
    cliente.invalidateQueries({ queryKey: ['caracteristicas'] });
  };

  const ativas = (lista.data ?? []).filter((c) => c.ativo);
  const inativas = (lista.data ?? []).filter((c) => !c.ativo);

  return (
    <Layout titulo="Características das peças">
      <p className="mb-4 text-sm text-mute">
        O que você criar aqui vira campo no cadastro de peça e opção de filtro na
        busca. Característica em <strong className="text-paper">lista de opções</strong> é
        a que mais ajuda no balcão: ela padroniza o preenchimento, e é isso que
        permite procurar “amortecedor Gol” e marcar “lado: direito”.
      </p>

      {lista.isLoading && <Carregando />}
      {lista.error && <Aviso tipo="erro">{mensagemDeErro(lista.error)}</Aviso>}

      {!abrindoNova && (
        <button type="button" className="botao-primario w-full" onClick={() => setAbrindoNova(true)}>
          Nova característica
        </button>
      )}
      {abrindoNova && (
        <Formulario
          aoCancelar={() => setAbrindoNova(false)}
          aoSalvar={() => { setAbrindoNova(false); recarregar(); }}
        />
      )}

      <div className="mt-6 space-y-3">
        {ativas.map((c) => (
          <Cartao key={c.id} caracteristica={c} aoMudar={recarregar} />
        ))}
        {!lista.isLoading && ativas.length === 0 && (
          <p className="text-sm text-dim">
            Nenhuma característica ainda. A posição/lado que já existe no cadastro
            continua funcionando — o que você criar aqui vem além dela.
          </p>
        )}
      </div>

      {inativas.length > 0 && (
        <div className="mt-8">
          <h2 className="rotulo mb-2">Desativadas</h2>
          <p className="mb-3 text-xs text-dim">
            Somem do cadastro e do filtro. O que já estava preenchido nas peças
            continua guardado e volta se você reativar.
          </p>
          <div className="space-y-3">
            {inativas.map((c) => (
              <Cartao key={c.id} caracteristica={c} aoMudar={recarregar} />
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
}

function Cartao({ caracteristica: c, aoMudar }: {
  caracteristica: ProductAttribute; aoMudar: () => void;
}) {
  const [editando, setEditando] = useState(false);

  const alternar = useMutation({
    mutationFn: () => atualizarCaracteristica(c.id, { ativo: !c.ativo }),
    onSuccess: aoMudar,
  });

  if (editando) {
    return (
      <Formulario
        inicial={c}
        aoCancelar={() => setEditando(false)}
        aoSalvar={() => { setEditando(false); aoMudar(); }}
      />
    );
  }

  return (
    <div className="cartao">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-paper">{c.nome}</p>
          <p className="text-xs text-dim">
            {TIPOS.find((t) => t.id === c.tipo)?.rotulo ?? c.tipo}
            {c.tipo === 'lista' && ` — ${c.opcoes.length} opç${c.opcoes.length === 1 ? 'ão' : 'ões'}`}
          </p>
          {c.tipo === 'lista' && c.opcoes.length > 0 && (
            <p className="mt-1 break-words text-sm text-mute">{c.opcoes.join(' · ')}</p>
          )}
        </div>
        <span className="shrink-0 text-xs text-dim">ordem {c.ordem}</span>
      </div>

      {alternar.error && (
        <div className="mt-3"><Aviso tipo="erro">{mensagemDeErro(alternar.error)}</Aviso></div>
      )}

      <div className="mt-3 flex gap-2">
        <button type="button" className="botao-secundario flex-1" onClick={() => setEditando(true)}>
          Editar
        </button>
        <button
          type="button"
          className="botao-secundario flex-1"
          disabled={alternar.isPending}
          onClick={() => alternar.mutate()}
        >
          {c.ativo ? 'Desativar' : 'Reativar'}
        </button>
      </div>
    </div>
  );
}

function Formulario({ inicial, aoCancelar, aoSalvar }: {
  inicial?: ProductAttribute; aoCancelar: () => void; aoSalvar: () => void;
}) {
  const [nome, setNome] = useState(inicial?.nome ?? '');
  const [tipo, setTipo] = useState<ProductAttributeType>(inicial?.tipo ?? 'lista');
  // As opções vivem como texto de uma linha por opção: é o jeito mais direto de
  // digitar quatro valores no celular sem uma UI de lista com botão de +.
  const [opcoes, setOpcoes] = useState((inicial?.opcoes ?? []).join('\n'));
  const [ordem, setOrdem] = useState(String(inicial?.ordem ?? 0));

  const listaOpcoes = opcoes.split('\n').map((o) => o.trim()).filter(Boolean);
  const invalido = !nome.trim()
    ? 'Dê um nome à característica.'
    : tipo === 'lista' && listaOpcoes.length === 0
      ? 'Lista de opções precisa de pelo menos uma opção.'
      : null;

  const salvar = useMutation({
    mutationFn: () => {
      const dados = {
        nome, tipo, opcoes: listaOpcoes,
        ordem: Number.parseInt(ordem, 10) || 0,
      };
      return inicial
        ? atualizarCaracteristica(inicial.id, dados)
        : criarCaracteristica(dados);
    },
    onSuccess: aoSalvar,
  });

  return (
    <div className="cartao mt-3 space-y-3">
      <div>
        <label className="rotulo" htmlFor="carac-nome">Nome</label>
        <input
          id="carac-nome"
          className="campo"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Lado, Voltagem, Rosca..."
          autoComplete="off"
          autoFocus
        />
      </div>

      <fieldset>
        <legend className="rotulo">Tipo</legend>
        <div className="space-y-2">
          {TIPOS.map((t) => (
            <label key={t.id} className="flex gap-2 text-sm">
              <input
                type="radio"
                name="carac-tipo"
                className="mt-1 shrink-0"
                checked={tipo === t.id}
                onChange={() => setTipo(t.id)}
              />
              <span>
                <span className="text-paper">{t.rotulo}</span>
                <span className="block text-xs text-dim">{t.resumo}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {tipo === 'lista' && (
        <div>
          <label className="rotulo" htmlFor="carac-opcoes">Opções — uma por linha</label>
          <textarea
            id="carac-opcoes"
            className="campo h-32"
            value={opcoes}
            onChange={(e) => setOpcoes(e.target.value)}
            placeholder={'direito\nesquerdo\ndianteiro\ntraseiro'}
          />
          <p className="mt-1 text-xs text-dim">
            Escreva do jeito que deve aparecer na tela. Repetidas são descartadas.
          </p>
        </div>
      )}

      <div>
        <label className="rotulo" htmlFor="carac-ordem">Ordem no formulário</label>
        <input
          id="carac-ordem"
          className="campo"
          value={ordem}
          inputMode="numeric"
          onChange={(e) => setOrdem(e.target.value)}
        />
        <p className="mt-1 text-xs text-dim">Menor aparece primeiro. Empate desempata por nome.</p>
      </div>

      {invalido && <Aviso tipo="alerta">{invalido}</Aviso>}
      {salvar.error && <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso>}

      <div className="flex gap-2">
        <button type="button" className="botao-secundario flex-1" onClick={aoCancelar}>
          Cancelar
        </button>
        <button
          type="button"
          className="botao-primario flex-1"
          disabled={!!invalido || salvar.isPending}
          onClick={() => salvar.mutate()}
        >
          {salvar.isPending ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
    </div>
  );
}
